import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { toast } from "sonner";
import { 
  ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock, 
  Phone, MessageSquare, Building2, Zap, Calendar,
  TrendingUp, Users, AlertTriangle, Play, Pause
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

export default function SyncDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  
  // Data states
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [ninetyAcresStatus, setNinetyAcresStatus] = useState(null);
  const [mcubeLogs, setMcubeLogs] = useState(null);
  const [doubleTickStats, setDoubleTickStats] = useState(null);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [schedulerRes, ninetyAcresRes, mcubeRes, dtTemplatesRes] = await Promise.all([
        axios.get(`${API}/scheduler/status`).catch(() => ({ data: null })),
        axios.get(`${API}/99acres/status`).catch(() => ({ data: null })),
        axios.get(`${API}/ivr/mcube/logs?limit=10`).catch(() => ({ data: null })),
        axios.get(`${API}/message-templates/approved`).catch(() => ({ data: null })),
      ]);

      setSchedulerStatus(schedulerRes.data);
      setNinetyAcresStatus(ninetyAcresRes.data);
      setMcubeLogs(mcubeRes.data);
      
      // Calculate DoubleTick stats from templates
      if (dtTemplatesRes.data) {
        const templates = dtTemplatesRes.data.templates || dtTemplatesRes.data || [];
        setDoubleTickStats({
          total_templates: templates.length,
          by_category: templates.reduce((acc, t) => {
            const cat = t.category || "other";
            acc[cat] = (acc[cat] || 0) + 1;
            return acc;
          }, {}),
          last_synced: templates[0]?.synced_at || null
        });
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load integration data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  const triggerSync = async (provider) => {
    setSyncing(prev => ({ ...prev, [provider]: true }));
    try {
      if (provider === "99acres") {
        const res = await axios.post(`${API}/99acres/sync`, { hours_back: 2 });
        toast.success(`99acres sync complete: ${res.data.leads_created} new, ${res.data.leads_reinquiry} re-inquiries`);
      } else if (provider === "doubletick") {
        await axios.post(`${API}/message-templates/sync`);
        toast.success("DoubleTick templates synced");
      } else if (provider === "scheduler") {
        await axios.post(`${API}/scheduler/run/99acres_sync`);
        toast.success("Manual sync triggered");
      }
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `${provider} sync failed`);
    } finally {
      setSyncing(prev => ({ ...prev, [provider]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (success) => {
    if (success === true) return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Healthy</Badge>;
    if (success === false) return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Error</Badge>;
    return <Badge className="bg-gray-100 text-gray-600"><Clock className="w-3 h-3 mr-1" />Unknown</Badge>;
  };

  if (loading && !schedulerStatus) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sync-dashboard-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Integration Sync Dashboard</h1>
                <p className="text-sm text-slate-500">Monitor and manage all external integrations</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchAllData}
              disabled={loading}
              data-testid="refresh-all-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh All
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Scheduler Status Card */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid="scheduler-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg">Background Scheduler</CardTitle>
                  <p className="text-sm text-slate-500">Automated sync jobs</p>
                </div>
              </div>
              <Badge className={schedulerStatus?.status === "running" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                {schedulerStatus?.status === "running" ? <Play className="w-3 h-3 mr-1" /> : <Pause className="w-3 h-3 mr-1" />}
                {schedulerStatus?.status || "Unknown"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {schedulerStatus?.jobs?.map(job => (
                <div key={job.id} className="bg-white/60 rounded-lg p-3 border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm text-slate-700">{job.name}</span>
                    <Badge variant="outline" className="text-xs">{job.trigger.split('[')[0]}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    Next run: {job.next_run ? new Date(job.next_run).toLocaleTimeString() : 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Integration Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* 99acres Card */}
          <Card className="border-orange-200" data-testid="ninetyacres-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">99acres</CardTitle>
                    <p className="text-xs text-slate-500">Property Portal Leads</p>
                  </div>
                </div>
                {getStatusBadge(ninetyAcresStatus?.sync_state?.last_sync_success)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {ninetyAcresStatus?.today_stats?.total_created || 0}
                    </p>
                    <p className="text-xs text-slate-500">New Today</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      {ninetyAcresStatus?.today_stats?.total_reinquiry || 0}
                    </p>
                    <p className="text-xs text-slate-500">Re-inquiries</p>
                  </div>
                </div>

                {/* Last Sync */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Last Sync</span>
                    <span className="font-medium">{formatDate(ninetyAcresStatus?.sync_state?.last_sync_at)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-500">Syncs Today</span>
                    <span className="font-medium">{ninetyAcresStatus?.today_stats?.total_syncs || 0}/6</span>
                  </div>
                </div>

                {/* Recent Syncs */}
                {ninetyAcresStatus?.recent_syncs?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-500">Recent Syncs</p>
                    {ninetyAcresStatus.recent_syncs.slice(0, 3).map((sync, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-white rounded p-2 border">
                        <span className="text-slate-600">{new Date(sync.started_at).toLocaleTimeString()}</span>
                        <span className={sync.success ? "text-green-600" : "text-red-600"}>
                          {sync.leads_created} new, {sync.leads_reinquiry} re-inq
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sync Button */}
                <Button 
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  onClick={() => triggerSync("99acres")}
                  disabled={syncing["99acres"]}
                  data-testid="sync-99acres-btn"
                >
                  {syncing["99acres"] ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Sync Now (Last 2 Hours)</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Mcube IVR Card */}
          <Card className="border-purple-200" data-testid="mcube-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Mcube IVR</CardTitle>
                    <p className="text-xs text-slate-500">Incoming Call Leads</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />Webhook Active
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {mcubeLogs?.total || 0}
                    </p>
                    <p className="text-xs text-slate-500">Total Calls</p>
                  </div>
                  <div className="bg-violet-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-violet-600">
                      {mcubeLogs?.logs?.filter(l => l.result?.status === "new_lead_created").length || 0}
                    </p>
                    <p className="text-xs text-slate-500">New Leads</p>
                  </div>
                </div>

                {/* Webhook URL */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-500 mb-1">Webhook URL</p>
                  <code className="text-xs text-purple-700 break-all">
                    /api/ivr/mcube/webhook
                  </code>
                </div>

                {/* Recent Calls */}
                {mcubeLogs?.logs?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-500">Recent Calls</p>
                    {mcubeLogs.logs.slice(0, 4).map((log, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-white rounded p-2 border">
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-600">...{log.processed_phone?.slice(-4)}</span>
                        </div>
                        <Badge variant="outline" className={
                          log.result?.status === "new_lead_created" 
                            ? "text-green-600 border-green-200" 
                            : "text-blue-600 border-blue-200"
                        }>
                          {log.result?.status === "new_lead_created" ? "New" : "Updated"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}

                {/* Info */}
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                  <p className="text-xs text-purple-700">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Mcube pushes calls automatically. No manual sync needed.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DoubleTick Card */}
          <Card className="border-green-200" data-testid="doubletick-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">DoubleTick</CardTitle>
                    <p className="text-xs text-slate-500">WhatsApp Messaging</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />Connected
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {doubleTickStats?.total_templates || 0}
                    </p>
                    <p className="text-xs text-slate-500">Templates</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-600">
                      {Object.keys(doubleTickStats?.by_category || {}).length}
                    </p>
                    <p className="text-xs text-slate-500">Categories</p>
                  </div>
                </div>

                {/* Categories breakdown */}
                {doubleTickStats?.by_category && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">Templates by Category</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(doubleTickStats.by_category).slice(0, 5).map(([cat, count]) => (
                        <Badge key={cat} variant="outline" className="text-xs">
                          {cat}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Template messaging</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Automation triggers</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Bulk messaging</span>
                  </div>
                </div>

                {/* Sync Button */}
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => triggerSync("doubletick")}
                  disabled={syncing["doubletick"]}
                  data-testid="sync-doubletick-btn"
                >
                  {syncing["doubletick"] ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Sync Templates</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Bar */}
        <Card data-testid="quick-stats-card">
          <CardContent className="py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Leads Today</p>
                  <p className="text-lg font-bold text-slate-800">
                    {(ninetyAcresStatus?.today_stats?.total_created || 0) + 
                     (mcubeLogs?.logs?.filter(l => l.result?.status === "new_lead_created" && 
                       new Date(l.created_at).toDateString() === new Date().toDateString()).length || 0)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">99acres Syncs</p>
                  <p className="text-lg font-bold text-slate-800">
                    {ninetyAcresStatus?.today_stats?.total_syncs || 0}/6 hourly
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">IVR Calls</p>
                  <p className="text-lg font-bold text-slate-800">
                    {mcubeLogs?.total || 0} total
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">WA Templates</p>
                  <p className="text-lg font-bold text-slate-800">
                    {doubleTickStats?.total_templates || 0} active
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
